import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, type TeamsResponse } from "../api";
import { useClubs } from "../ClubContext";

export function TeamsPage() {
  const navigate = useNavigate();
  const { activeClub, isActiveClubAdmin: isAdmin, loading: clubsLoading } = useClubs();
  const [data, setData] = useState<TeamsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const [addMessage, setAddMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const clubId = activeClub?.clubId ?? null;
  // Short prefix for examples: first word of the club name (e.g. "680 Water
  // Polo" -> "680"), so suggested team names read "680 Red" not "680 Water
  // Polo Red".
  const shortClub = (activeClub?.clubName || "680").split(" ")[0];

  const loadTeams = useCallback(
    async (id: string) => {
      try {
        const res = await api<TeamsResponse>(`/v1/clubs/${id}/teams`);
        setData(res);
        setError(null);
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Failed to load";
        setError(msg);
        if (msg.toLowerCase().includes("unauthor")) navigate("/login", { replace: true });
      }
    },
    [navigate]
  );

  useEffect(() => {
    if (!clubId || !isAdmin) return;
    setData(null);
    setAddMessage(null);
    loadTeams(clubId);
  }, [clubId, isAdmin, loadTeams]);

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!clubId) return;
    const name = newName.trim();
    if (!name) return;
    setAdding(true);
    setAddMessage(null);
    try {
      await api(`/v1/clubs/${clubId}/teams`, {
        method: "POST",
        body: JSON.stringify({ name }),
      });
      setAddMessage({ ok: true, text: `Added ${name}` });
      setNewName("");
      await loadTeams(clubId);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Add failed";
      setAddMessage({ ok: false, text: msg });
    } finally {
      setAdding(false);
    }
  }

  async function handleDelete(teamId: string, name: string) {
    if (!clubId) return;
    if (!confirm(`Delete the team "${name}"? Games already recorded keep their name.`)) return;
    try {
      await api(`/v1/clubs/${clubId}/teams/${teamId}`, { method: "DELETE" });
      await loadTeams(clubId);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Delete failed");
    }
  }

  return (
    <main className="mx-auto max-w-6xl space-y-8 px-6 py-8">
        <section>
          <h2 className="mb-2 text-lg font-semibold">
            Teams{activeClub && ` — ${activeClub.clubName}`}
          </h2>
          <p className="mb-4 text-sm text-slate-500">
            Define this club's teams (e.g. {shortClub} Red, {shortClub} Blue).
            Coaches pick one when they start a game in the app.
          </p>
          {error && <p className="mb-4 text-sm text-rose-600">{error}</p>}
          {!isAdmin && !clubsLoading && !error && (
            <p className="text-sm text-slate-500">
              You need to be a club admin of {activeClub?.clubName ?? "this club"} to view this page.
            </p>
          )}
          {isAdmin && data === null && !error && (
            <p className="text-sm text-slate-500">Loading…</p>
          )}
          {isAdmin && data && (
            <div className="rounded-xl border border-slate-200 bg-white">
              {data.teams.length === 0 ? (
                <p className="px-4 py-6 text-sm text-slate-500">No teams yet. Add one below.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="px-4 py-2">Team</th>
                      <th className="px-4 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.teams.map((t) => (
                      <tr key={t.id} className="border-t border-slate-100">
                        <td className="px-4 py-3 font-medium">{t.name}</td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => handleDelete(t.id, t.name)}
                            className="text-xs font-medium text-rose-600 hover:underline"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </section>

        {isAdmin && data && (
          <section>
            <h2 className="mb-4 text-lg font-semibold">Add a team</h2>
            <form onSubmit={handleAdd} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                <div className="flex-1">
                  <label className="block text-xs font-semibold text-slate-600">Team name</label>
                  <input
                    type="text"
                    required
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    className="mt-1 w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm"
                    placeholder={`${shortClub} Red`}
                  />
                </div>
                <button
                  type="submit"
                  disabled={adding}
                  className="rounded-md bg-brand-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
                >
                  {adding ? "Adding…" : "Add team"}
                </button>
              </div>
              {addMessage && (
                <p
                  className={`mt-3 text-sm ${
                    addMessage.ok ? "text-emerald-600" : "text-rose-600"
                  }`}
                >
                  {addMessage.text}
                </p>
              )}
            </form>
          </section>
        )}
    </main>
  );
}
